import { useState } from 'react';
import { TIMING_NUTRIENTS } from '@/lib/nutrientDistribution';

/**
 * Per-device "Nutrient timing" preference — which nutrients (up to 3) the
 * patient Summary's Nutrient-timing card charts. Saved like the theme / font /
 * thumbnail prefs; edited on the Profile page.
 *
 * Read-on-mount is enough (no live cross-page sync needed): the editor lives on
 * Profile and the Summary feed remounts when the clinician returns to a patient
 * tab, so it always picks up the current value.
 */
const STORAGE_KEY = 'ui.nutrientTiming';
export const NUTRIENT_TIMING_MAX = 3;
const DEFAULT_KEYS = ['kcal'];
const VALID_KEYS = new Set(TIMING_NUTRIENTS.map((n) => n.key));

const sanitize = (keys: unknown): string[] => {
  if (!Array.isArray(keys)) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const k of keys) {
    if (typeof k === 'string' && VALID_KEYS.has(k) && !seen.has(k)) {
      seen.add(k);
      out.push(k);
    }
  }
  return out.slice(0, NUTRIENT_TIMING_MAX);
};

export const readNutrientTimingPrefs = (): string[] => {
  if (typeof window === 'undefined') return DEFAULT_KEYS;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_KEYS;
    const cleaned = sanitize(JSON.parse(raw));
    return cleaned.length ? cleaned : DEFAULT_KEYS;
  } catch {
    return DEFAULT_KEYS;
  }
};

export const useNutrientTimingPrefs = (): [string[], (next: string[]) => void] => {
  const [value, setValue] = useState<string[]>(readNutrientTimingPrefs);
  const set = (next: string[]) => {
    const cleaned = sanitize(next);
    setValue(cleaned);
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(cleaned));
    } catch {
      /* private mode / quota — ignore */
    }
  };
  return [value, set];
};
